import { centralDbRequest, getCentralDbUrls } from './centralDbClient';

export interface DocumentApproval {
  id: string;
  title: string;
  description: string | null;
  documentType: string;
  workerId: string;
  worker?: {
    id: string;
    firstName?: string;
    prefix?: string;
    lastName?: string;
    email?: string;
    easyflexRegistrationNumber?: string;
  };
  fileUrl: string | null;
  fileType: string | null;
  htmlContent: string | null;
  status: 'pending' | 'approved' | 'rejected' | 'expired';
  rejectionReason: string | null;
  approvedAt: string | null;
  rejectedAt: string | null;
  expiresAt: string | null;
  signatureData: string | null;
  magicLinkToken?: string | null;
  approvalUrl?: string;
  warnings?: string[];
  createdAt: string;
  updatedAt: string;
}

export interface WorkerOption {
  id: string;
  firstName?: string;
  prefix?: string;
  lastName?: string;
  email?: string;
  easyflexRegistrationNumber?: string;
}

export async function fetchApprovals(
  token: string,
  filters?: { workerId?: string; status?: string },
): Promise<DocumentApproval[]> {
  const res = await centralDbRequest({
    method: 'GET',
    path: '/document-approvals',
    query: filters,
    token,
  });
  if (!res.ok) throw new Error(res.data?.message || `HTTP ${res.status}`);
  return res.data;
}

export async function fetchApproval(token: string, id: string): Promise<DocumentApproval> {
  const res = await centralDbRequest({
    method: 'GET',
    path: `/document-approvals/${id}`,
    token,
  });
  if (!res.ok) throw new Error(res.data?.message || `HTTP ${res.status}`);
  return res.data;
}

export async function createApproval(
  token: string,
  data: {
    title: string;
    documentType: string;
    workerId: string;
    description?: string;
    fileUrl?: string;
    fileType?: string;
    htmlContent?: string;
    expiresAt?: string;
    workerEmail?: string;
    templateId?: string;
    language?: string;
    companyName?: string;
    templateData?: {
      fields?: Record<string, string>;
      photoUrls?: string[];
    };
  },
): Promise<DocumentApproval> {
  const res = await centralDbRequest({
    method: 'POST',
    path: '/document-approvals',
    body: data,
    token,
  });
  if (!res.ok) throw new Error(res.data?.message || `HTTP ${res.status}`);
  return res.data;
}

export async function cancelApproval(token: string, id: string): Promise<void> {
  const res = await centralDbRequest({
    method: 'DELETE',
    path: `/document-approvals/${id}`,
    token,
  });
  if (!res.ok) throw new Error(res.data?.message || `HTTP ${res.status}`);
}

export async function searchWorkers(token: string, query: string): Promise<WorkerOption[]> {
  const res = await centralDbRequest({
    method: 'GET',
    path: '/workers/options',
    query: { search: query, limit: 50 },
    token,
  });
  if (!res.ok) throw new Error(res.data?.message || `HTTP ${res.status}`);
  return res.data;
}

export async function uploadDocument(
  token: string,
  file: File,
): Promise<{ fileUrl: string; fileType: string }> {
  const urls = getCentralDbUrls();
  const formData = new FormData();
  formData.append('file', file);

  const res = await fetch(`${urls.apiBaseUrl}/document-approvals/upload`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });

  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new Error(body?.message || `Upload failed: HTTP ${res.status}`);
  }
  return res.json();
}
